/* eslint-disable react/no-danger */
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { FormattedMessage, useIntl } from 'react-intl';
import { Icon } from '@unite-us/app-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ProgramInfo from './ProgramInfo/ProgramInfo';
import programPropsShape, { defaultProgramProps } from './programPropsShape';
import { isRichTextEmpty } from '../../hooks/utils/formatProgramInfo';
import groupOpeningHours from '../../utils/hoursUtils';
import { SERVICE_AREA_TYPE_NATIONAL, SERVICE_AREA_TYPE_STATE } from '../../utils/constants';
// Import colors directly since the module import may not work with React 16
const colors = {
  textBlue: '#182A44',
};

const displayName = 'uu-program-card';

function ProgramSearch({
  accessibility_options,
  age_requirements,
  age_specializations,
  city_requirements,
  county_requirements,
  delivery_options,
  feeScheduleProgram,
  gender_requirements,
  languages,
  population_restrictions,
  population_specializations,
  postal_code_requirements,
  state_requirements,
  address,
  description,
  distance,
  eligibilityText,
  email,
  hours,
  name,
  name_untranslated,
  phone,
  providerName,
  providerNameUntranslated,
  website,
  payment_options,
  transportation_options,
  service_areas,
  offers_specialized_screening,
}) {
  const intl = useIntl();

  const { cerb1519ScreeningRequiredFeeSchedules } = useFlags();
  const programName = name_untranslated ? `${name} | ${name_untranslated}` : name;
  return (
    <div className={classNames(displayName, 'mb-6 border border-text-blue')}>
      <header className={`${displayName}--title header-container`}>
        <h2>{programName}</h2>
        <div className={`${displayName}--title--details`}>
          <div className={`${displayName}--title--provided-by`}>
            <span className={`${displayName}--title--provided-by__at`}>
              <FormattedMessage
                id="ProgramSearch.providedby"
                defaultMessage="at"
              />
            </span>
            <span className={`${displayName}--title--provided-by__name`}>{providerName}</span>
            {providerNameUntranslated && (
              <>
                <span className={`${displayName}--title--provided-by__at`}> | at</span>
                <span className={`${displayName}--title--provided-by__name`}>{providerNameUntranslated}</span>
              </>
            )}
          </div>
          {distance && !service_areas && (
            <div className={`${displayName}--title--distance`}>
              {distance.replace('mi', intl.formatMessage({ id: 'miles', defaultMessage: 'miles' }))}
            </div>
          )}
        </div>
      </header>
      <div className={`${displayName}--description_section`}>
        {description && (
          <div
            className={`${displayName}--description`}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
          />
        )}
        <div className={`${displayName}--contact-info`}>
          {phone && (
            <div className={`${displayName}--contact-info__item`}>
              <Icon className="icon" color={colors.textBlue} icon="Phone" />
              <strong>{phone}</strong>
            </div>
          )}
          {email && (
            <div className={`${displayName}--contact-info__item`}>
              <Icon className="icon" color={colors.textBlue} icon="Email" />
              <strong>{email}</strong>
            </div>
          )}
          {website && (
            <div className={`${displayName}--contact-info__item`}>
              <Icon className="icon" color={colors.textBlue} icon="Website" />
              <strong>{website}</strong>
            </div>
          )}
          {address && (
            <div className={`${displayName}--contact-info__item`}>
              <Icon className="icon" color={colors.textBlue} icon="MapPin" />
              <strong>{address}</strong>
            </div>
          )}
          {service_areas && (
            <div className={`${displayName}--contact-info__item font-bold`}>
              <Icon className="icon" color={colors.textBlue} icon="Radar" />
              <strong>
                {intl.formatMessage({ id: 'Serves', defaultMessage: 'Serves: ' })}
                {' '}
                {service_areas[0].service_area_type === SERVICE_AREA_TYPE_NATIONAL
                  ? 'U.S.' : service_areas.map((SA) => SA.state_abbreviation).join(', ')}
              </strong>
            </div>
          )}
        </div>
        {hours && Object.keys(hours).length > 0 && (
          <div className={`${displayName}--contact-info__item`}>
            <Icon className="icon clock" color={colors.textBlue} icon="Clock" />
            <span>{intl.formatMessage({ id: 'Hours', defaultMessage: 'Hours:' })}</span>
            <span>{groupOpeningHours(hours, intl)}</span>
          </div>
        )}
      </div>
      {offers_specialized_screening && (
        <div className={`${displayName}--description_section`}>
          <div className="flex items-center gap-1 mb-2">
            <Icon color={colors.textBlue} icon="FileTextOFilled" />
            <strong>
              <FormattedMessage
                id="ProgramSearch.screening.title"
                defaultMessage="Offers specialized screening"
              />
            </strong>
          </div>
          <span>
            <FormattedMessage
              id="ProgramSearch.screening.description"
              // eslint-disable-next-line max-len
              defaultMessage="This program offers a specialized screening to make sure you're eligible for certain Medicaid-funded programs."
            />
          </span>
        </div>
      )}
      {cerb1519ScreeningRequiredFeeSchedules?.includes(feeScheduleProgram?.data?.document.fee_schedule.id) && (
        <div className={`${displayName}--description_section`}>
          <div className="flex items-center gap-1 mb-2">
            <Icon color={colors.textBlue} icon="ExclamationTriangle" />
            <strong>
              <FormattedMessage
                id="ProgramSearch.specialized.screening.title"
                defaultMessage="Requires specialized screening"
              />
            </strong>
          </div>
          <span>
            <FormattedMessage
              id="ProgramSearch.specialized.screening.description"
              // eslint-disable-next-line max-len
              defaultMessage="This program requires that you work with a case manager to fill out a specialized screening before receiving services."
            />
          </span>
        </div>
      )}
      <ProgramInfo
        accessibility_options={accessibility_options}
        age_requirements={age_requirements}
        age_specializations={age_specializations}
        city_requirements={city_requirements}
        county_requirements={county_requirements}
        delivery_options={delivery_options}
        displayName={displayName}
        gender_requirements={gender_requirements}
        languages={languages}
        payment_options={payment_options}
        population_restrictions={population_restrictions}
        population_specializations={population_specializations}
        postal_code_requirements={postal_code_requirements}
        state_requirements={state_requirements}
        transportation_options={transportation_options}
      />
      {isRichTextEmpty(eligibilityText) && (
        <div className={`${displayName}--eligibility`}>
          <strong>
            <FormattedMessage
              id="ProgramSearch.eligibilityInfo"
              defaultMessage="Other Eligibility Information:"
            />
          </strong>
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(eligibilityText),
            }}
          />
        </div>
      )}
    </div>
  );
}

ProgramSearch.propTypes = {
  ...programPropsShape,
  name: PropTypes.string.isRequired,
  name_untranslated: PropTypes.string,
  description: PropTypes.string.isRequired,
  distance: PropTypes.string,
  providerName: PropTypes.string.isRequired,
  providerNameUntranslated: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  website: PropTypes.string,
  address: PropTypes.string,
  hours: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        opens: PropTypes.string.isRequired,
        closes: PropTypes.string.isRequired,
      }),
    ),
  ),
  service_areas: PropTypes.arrayOf(PropTypes.shape(
    {
      service_area_type: PropTypes.oneOf([SERVICE_AREA_TYPE_NATIONAL, SERVICE_AREA_TYPE_STATE]),
      state_abbreviation: PropTypes.string,
    },
  )),
  offers_specialized_screening: PropTypes.bool,
};

ProgramSearch.defaultProps = {
  ...defaultProgramProps,
  distance: '',
  email: '',
  phone: '',
  website: '',
  address: '',
  hours: null,
  service_areas: undefined,
  name_untranslated: '',
  providerNameUntranslated: '',
  offers_specialized_screening: false,
};

export default ProgramSearch;
